import React, { useState } from 'react';
import { motion } from 'framer-motion';
import useClickOutside from '../../../../hooks/useClickOutside';
import { SavedLead } from '../../../../services/leadsService';
import { useTranslation } from 'react-i18next';

interface Lead extends SavedLead { }

interface LeadsSelectAllDropdownProps {
  allLeads: Lead[];
  selectedLeads: string[]; // Alterado para string[]
  setSelectedLeads: (ids: string[]) => void; // Alterado para string[]
}

const LeadsSelectAllDropdown: React.FC<LeadsSelectAllDropdownProps> = ({
  allLeads,
  selectedLeads,
  setSelectedLeads,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false));

  const handleSelect = (type: 'all' | 'whatsapp_only' | 'whatsapp_unprospected') => {
    let newSelection: string[] = [];

    // Leads com WhatsApp são aqueles com status 'Validado' ou 'Pendente'
    const isWhatsappLead = (lead: Lead) => lead.whatsapp_status === 'Validado' || lead.whatsapp_status === 'Pendente';

    switch (type) {
      case 'all':
        newSelection = allLeads.map(r => r.id);
        break;
      case 'whatsapp_only':
        newSelection = allLeads.filter(isWhatsappLead).map(r => r.id);
        break;
      case 'whatsapp_unprospected':
        newSelection = allLeads.filter(r => isWhatsappLead(r) && !r.prospected).map(r => r.id);
        break;
    }

    // Se a seleção atual for idêntica à nova seleção, desmarque tudo.
    const currentSelectionSet = new Set(selectedLeads);
    const newSelectionSet = new Set(newSelection);

    const isAlreadySelected = newSelection.length > 0 && newSelection.every(id => currentSelectionSet.has(id)) && currentSelectionSet.size === newSelectionSet.size;

    setSelectedLeads(isAlreadySelected ? [] : newSelection);
    setIsOpen(false);
  };

  const whatsappLeads = allLeads.filter(l => l.whatsapp_status === 'Validado' || l.whatsapp_status === 'Pendente');
  const whatsappUnprospectedLeads = allLeads.filter(l => (l.whatsapp_status === 'Validado' || l.whatsapp_status === 'Pendente') && !l.prospected);

  const selectionOptions = [
    {
      label: t('dashboard.select_option_all'),
      type: 'all' as const,
      count: allLeads.length,
      action: () => handleSelect('all')
    },
    {
      label: t('dashboard.select_option_whatsapp'),
      type: 'whatsapp_only' as const,
      count: whatsappLeads.length,
      action: () => handleSelect('whatsapp_only')
    },
    {
      label: t('dashboard.select_option_whatsapp_unprospected'),
      type: 'whatsapp_unprospected' as const,
      count: whatsappUnprospectedLeads.length,
      action: () => handleSelect('whatsapp_unprospected')
    },
  ];

  const isAllSelected = selectedLeads.length === allLeads.length && allLeads.length > 0;
  const isPartiallySelected = selectedLeads.length > 0 && !isAllSelected;

  const buttonText = isAllSelected
    ? t('dashboard.select_all_selected')
    : isPartiallySelected
      ? t('dashboard.select_partial', { count: selectedLeads.length })
      : t('dashboard.select_all');

  return (
    <div className="relative flex items-center gap-2" ref={dropdownRef}>

      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 dark:text-white bg-gray-100 dark:bg-background-dark-elevated2 hover:bg-gray-200 dark:hover:bg-background-dark-elevated3 transition-colors border border-transparent dark:border-primary-darkMode"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {buttonText}
        <span className="material-symbols-outlined text-lg">
          expand_more
        </span>
      </motion.button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-background-dark-elevated rounded-lg shadow-lg border border-gray-200 dark:border-primary-darkMode z-50 origin-top-right"
        >
          <div className="p-2">
            {selectionOptions.map((option) => (
              <motion.button
                key={option.type}
                onClick={option.action}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-background-dark-elevated2 rounded-md transition-colors"
                whileHover={{ x: 2 }}
              >
                <span>{option.label}</span>
                <span className="text-xs font-semibold text-primary dark:text-orange-300">{option.count}</span>
              </motion.button>
            ))}
            {selectedLeads.length > 0 && (
              <motion.button
                onClick={() => {
                  setSelectedLeads([]);
                  setIsOpen(false);
                }}
                className="w-full flex items-center justify-center mt-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors border-t border-gray-100 dark:border-gray-700/50"
                whileHover={{ scale: 1.01 }}
              >
                {t('dashboard.clear_selection', { count: selectedLeads.length })}
              </motion.button>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default LeadsSelectAllDropdown;